import {Injectable} from "@angular/core";
import {UserSession} from "../interfaces/domain/UserSession";
import {HttpRepository} from "./http.repository";
import {AppEventBus} from "../event-buses/app.event-bus";
import {AppEvent} from "../event-buses/AppEvent";
import {AppEventName} from "../event-buses/AppEventName";
import {User} from "../interfaces/rest/User";

@Injectable({
  providedIn: 'root',
})
export class UserSessionRepository {

  private userSession?: UserSession;
  private impersonatedUserSession?: UserSession;

  constructor(private appEventBus: AppEventBus) {

  }

  setImpersonation(userSession: UserSession) {
    this.impersonatedUserSession = userSession;
    this.appEventBus.dispatch(new AppEvent(AppEventName.NewUserSession, Object.assign(new UserSession(), userSession)));
  }

  removeImpersonation() {
    this.impersonatedUserSession = undefined;
    this.appEventBus.dispatch(new AppEvent(AppEventName.NewUserSession, Object.assign(new UserSession(), this.userSession)));
  }

  isImpersonation(): boolean {
    return !!this.impersonatedUserSession;
  }

  createUserSession(user: User): UserSession {
    const userSession = new UserSession();
    userSession.userId = user.userId;
    userSession.userType = user.isSee ? 'see' : 'subuser';
    userSession.email = user.email;
    userSession.firstName = user.firstName;
    userSession.lastName = user.lastName;
    if(user.userRole && user.userRole.keyName) {
      userSession.roles = [user.userRole.keyName];
    }
    userSession.isPasswordPrivate = user.isPasswordPrivate;
    userSession.agreement = user.isTermsAndConditionsAccepted;
    userSession.avatarUrl = user.profileImage?.fileURL;
    userSession.clientLogo = user.company?.companyLogo?.fileURL;
    userSession.languageId = user.languageId;
    userSession.languageCode = user.languageCode || 'en';
    userSession.clientId = user.company?.companyId?.toString();
    userSession.dateConfigObject = user.dateFormat;
    userSession.timeConfigObject = user.timeFormat;
    userSession.phoneNumber = user.phoneNumber;

    this.setUserSession(userSession);

    return userSession;
  }

  setUserSession(userSession: UserSession|undefined) {
    if(userSession) {
      if(this.impersonatedUserSession) {
        this.impersonatedUserSession = userSession;
      }
      else {
        this.userSession = userSession;
      }

      this.appEventBus.dispatch(new AppEvent(AppEventName.NewUserSession, Object.assign(new UserSession(), userSession)));
    }
    else {
      this.userSession = undefined;
      this.impersonatedUserSession = undefined;
    }
  }

  getUserSession() {
    if(this.impersonatedUserSession) {
      return this.impersonatedUserSession;
    }
    else {
      return this.userSession;
    }
  }

  isLoggedIn(): boolean {
    if(this.userSession || this.impersonatedUserSession) {
      return true;
    }
    else {
      return false;
    }
  }

  setFirstLogin(value: boolean) {
    if(value) {
      localStorage.setItem('__firstLogin', 'true');
    }
    else {
      localStorage.removeItem('__firstLogin');
    }
  }

  getFirstLogin(): boolean {
    const value = localStorage.getItem('__firstLogin');

    if(value === 'true') {
      return true;
    }
    else {
      return false;
    }
  }

  setViewingTerms(viewingTerms: boolean) {
    if(viewingTerms) {
      localStorage.setItem('__viewingTerms', 'true');
    }
    else {
      localStorage.removeItem('__viewingTerms');
    }
  }

  getViewingTerms(): boolean {
    const value = localStorage.getItem('__viewingTerms');

    if(value === 'true') {
      return true;
    }
    else {
      return false;
    }
  }

  isSeeUser(): boolean {
    const userSession = this.getUserSession();

    if(userSession && userSession.userType === 'see') {
      return true;
    }

    return false;
  }

  clearLocalStorage(): void {
    localStorage.removeItem('__viewingTerms');
    localStorage.removeItem('__firstLogin');
  }
}
